import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import Alert from '../../../components/Alert/Alert';
import { AppSelect } from '../../../components/common/select';
import api from '../../../api';
import { getUserLeads } from '../api/taskApi';
import { useAuth } from '../../../contexts/AuthProvider';
import useModalEffect from '../../../hooks/useModalEffect';

const TaskForm = memo(({ isOpen, onClose, onSubmit, initialData, disableCompanyField = false, hasViewOwnPermission = false, hasViewGlobalPermission = true, defaultClientId = null }) => {
    const { user } = useAuth();
    
    // ✅ PERFORMANCE: Use React Hook Form for optimized form state management
    const { control, handleSubmit, reset, watch, setValue, setError, getValues, formState: { errors } } = useForm({
        defaultValues: {
            clientName: '',
            contactName: '',
            projectName: '',
            assignedUser: '',
            assignedUserId: null,
            taskTitle: '',
            type: '',
            dueDate: '',
            priority: '',
            status: '',
            description: '',
            leadId: null,
            documents: []
        },
        mode: 'onBlur',
        shouldUnregister: false
    });

    // Watch form values for effects (doesn't cause re-renders)
    const watchedClient = watch('clientName');
    
    const [alert, setAlert] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Dynamic options from API
    const [clientOptions, setClientOptions] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [allLeads, setAllLeads] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [contactOptions, setContactOptions] = useState([]);

    const priorityOptions = [
        { value: 'high', label: 'High', name: 'High' },
        { value: 'medium', label: 'Medium', name: 'Medium' },
        { value: 'low', label: 'Low', name: 'Low' }
    ];

    const statusOptions = [
        { value: 'to do', label: 'To Do', name: 'To Do' },
        { value: 'in progress', label: 'In Progress', name: 'In Progress' },
        { value: 'done', label: 'Done', name: 'Done' }
    ];

    // ✅ PERFORMANCE: Memoize fetch functions to prevent recreation
    const fetchClientOptions = useCallback(async () => {
        try {
            const companiesRes = await api.get('/md/companies').catch(() => ({ data: [] }));
            setClientOptions((companiesRes.data || []).map(c => ({ 
                value: c.id.toString(), 
                label: c.companyName,
                name: c.companyName
            })));
        } catch (error) {
            setClientOptions([]);
        }
    }, []);

    const fetchUserOptions = useCallback(async () => {
        try {
            const usersRes = await api.get('/md/internal-users/option-internal-user').catch(() => ({ data: [] }));
            setUserOptions((usersRes.data || []).map(user => ({
                value: user.id.toString(),
                label: user.fullName || `${user.firstName} ${user.lastName}`.trim(),
                name: user.fullName || `${user.firstName} ${user.lastName}`.trim()
            })));
        } catch (error) {
            setUserOptions([]);
        }
    }, []);

    const fetchTypeOptions = useCallback(async () => {
        try {
            const taskTypesRes = await api.get('/md/task-type').catch(() => ({ data: [] }));
            const taskTypesData = taskTypesRes.data || [];
            if (taskTypesData.length > 0) {
                setTypeOptions(taskTypesData.map(taskType => ({
                    value: taskType.taskType, // Keep original title case format
                    label: taskType.taskType,
                    name: taskType.taskType
                })));
            } else {
                setTypeOptions([
                    { value: 'Call', label: 'Call', name: 'Call' },
                    { value: 'Meeting', label: 'Meeting', name: 'Meeting' },
                    { value: 'Email', label: 'Email', name: 'Email' },
                    { value: 'Follow Up', label: 'Follow Up', name: 'Follow Up' }
                ]);
            }
        } catch (error) {
            setTypeOptions([
                { value: 'Call', label: 'Call', name: 'Call' },
                { value: 'Meeting', label: 'Meeting', name: 'Meeting' },
                { value: 'Email', label: 'Email', name: 'Email' },
                { value: 'Follow Up', label: 'Follow Up', name: 'Follow Up' }
            ]);
        }
    }, []);

    // ✅ PERFORMANCE: Memoize fetch functions to prevent recreation
    const fetchProjectOptions = useCallback(async (clientId) => {
        if (!clientId) {
            setProjectOptions([]);
            setContactOptions([]);
            setAllLeads([]);
            return;
        }

        try {
            // Fetch leads and contacts in parallel
            const [leadsResponse, contactsResponse] = await Promise.allSettled([
                api.get(`/crm/leads/by-company-id/${clientId}`),
                api.get(`/crm/contacts/by-company/${clientId}`)
            ]);
            
            // Process leads
            let leads = [];
            if (leadsResponse.status === 'fulfilled') {
                leads = leadsResponse.value.data || [];
                
                // ✅ Filter leads if user has view_own permission (and not view_global)
                if (!hasViewGlobalPermission && hasViewOwnPermission && user?.id) {
                    leads = leads.filter(lead => lead.assignedUserId === user.id);
                }
            }
            
            setAllLeads(leads);
            
            setProjectOptions(leads.map(lead => ({ 
                value: lead.id.toString(), 
                label: lead.projectName || lead.companyName || 'Unnamed Project',
                name: lead.projectName || lead.companyName || 'Unnamed Project'
            })));
            
            // Process contacts - merge contacts from API and from leads
            const uniqueContacts = [];
            const seenContactIds = new Set();
            
            // First, add contacts from the company API (direct contacts)
            if (contactsResponse.status === 'fulfilled' && contactsResponse.value.data) {
                contactsResponse.value.data.forEach(contact => {
                    if (contact.id && !seenContactIds.has(contact.id)) {
                        seenContactIds.add(contact.id);
                        uniqueContacts.push({
                            value: contact.id.toString(),
                            label: contact.name || 'Unknown Contact',
                            name: contact.name || 'Unknown Contact'
                        });
                    }
                });
            }
            
            // Then, add contacts from leads (in case some contacts are only in leads)
            leads.forEach(lead => {
                if (lead.contactId && !seenContactIds.has(lead.contactId)) {
                    seenContactIds.add(lead.contactId);
                    uniqueContacts.push({
                        value: lead.contactId.toString(),
                        label: lead.pic || lead.contactName || 'Unknown Contact',
                        name: lead.pic || lead.contactName || 'Unknown Contact'
                    });
                }
            });
            
            setContactOptions(uniqueContacts);
        } catch (error) {
            console.error('Error loading projects and contacts:', error);
            setProjectOptions([]);
            setAllLeads([]);
            setContactOptions([]);
        }
    }, [hasViewGlobalPermission, hasViewOwnPermission, user?.id]);

    useModalEffect(isOpen);

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 3000);
    };

    // ✅ PERFORMANCE: Handle edit mode - reset form with editing data
    const isInitializingRef = useRef(false);
    useEffect(() => {
        const initializeEditData = async () => {
            if (isOpen && initialData && !isInitializingRef.current) {
                isInitializingRef.current = true;
                setIsLoadingData(true);

                // Load all dropdown options in parallel
                await Promise.all([
                    fetchClientOptions(),
                    fetchUserOptions(),
                    fetchTypeOptions(),
                ]);

                const transformedData = {
                    clientName: initialData.clientId ? initialData.clientId.toString() : (initialData.clientName || ''),
                    contactName: initialData.contactId ? initialData.contactId.toString() : '',
                    projectName: initialData.leadId ? initialData.leadId.toString() : '',
                    assignedUser: initialData.assignedUserId ? initialData.assignedUserId.toString() : '',
                    assignedUserId: initialData.assignedUserId || null,
                    taskTitle: initialData.taskTitle || '',
                    type: initialData.type || '',
                    dueDate: initialData.dueDate ? initialData.dueDate.split('T')[0] : '',
                    priority: initialData.priority || '',
                    status: initialData.status || '',
                    description: initialData.description || '',
                    leadId: initialData.leadId || null,
                    documents: []
                };

                reset(transformedData);

                // Set uploaded files if editing
                if (initialData.file || initialData.fileName) {
                    const existingFile = {
                        name: initialData.fileName || 'existing-file.pdf',
                        size: 0,
                        type: 'application/pdf',
                        url: `/api/crm/lead-tasks/download/${initialData.id}`,
                        isExisting: true
                    };
                    setDocuments([existingFile]);
                }

                // Load projects and contacts for the selected client
                if (transformedData.clientName) {
                    await fetchProjectOptions(transformedData.clientName);
                    // After projects are loaded, ensure projectName is set
                    if (transformedData.projectName) {
                        setTimeout(() => {
                            setValue('projectName', transformedData.projectName, { shouldValidate: false, shouldDirty: false });
                        }, 200);
                    }
                }

                setIsLoadingData(false);
                isInitializingRef.current = false;
            } else if (isOpen && !initialData && !isInitializingRef.current) {
                isInitializingRef.current = true;
                setIsLoadingData(true);

                // Load all dropdown options in parallel
                await Promise.all([
                    fetchClientOptions(),
                    fetchUserOptions(),
                    fetchTypeOptions(),
                ]);

                // ✅ If creating new task with view_own permission, auto-set assigned user to current user
                const autoAssignedUserId = (!hasViewGlobalPermission && hasViewOwnPermission && user?.id)
                    ? user.id.toString()
                    : '';
                const autoAssignedUserIdNum = (!hasViewGlobalPermission && hasViewOwnPermission && user?.id) ? user.id : null;

                // Preselected client for create mode (e.g. the Tasks tab of a
                // company detail page). Passing it here rather than through
                // initialData keeps this the create branch — otherwise the
                // modal is titled "Edit Task" and the auto-assign above is skipped.
                const presetClientName = defaultClientId ? String(defaultClientId) : '';

                const formValues = {
                    clientName: presetClientName,
                    contactName: '',
                    projectName: '',
                    assignedUser: autoAssignedUserId,
                    assignedUserId: autoAssignedUserIdNum,
                    taskTitle: '',
                    type: '',
                    dueDate: '',
                    priority: '',
                    status: '',
                    description: '',
                    leadId: null,
                    documents: []
                };

                reset(formValues);
                setDocuments([]);
                if (presetClientName) {
                    await fetchProjectOptions(presetClientName);
                } else {
                    setProjectOptions([]);
                }
                setIsLoadingData(false);
                isInitializingRef.current = false;
            }
        };

        initializeEditData();
    }, [isOpen, initialData?.id, initialData?.clientId, initialData?.leadId, defaultClientId, fetchClientOptions, fetchUserOptions, fetchTypeOptions, fetchProjectOptions, reset, setValue, hasViewGlobalPermission, hasViewOwnPermission, user?.id]);

    // ✅ PERFORMANCE: Handle AppSelect changes (client, project, etc.)
    const handleSelectChange = useCallback((name, value) => {
        setValue(name, value, { shouldValidate: false });
        
        if (name === 'clientName') {
            fetchProjectOptions(value);
            // Clear project and contact when client changes
            setValue('projectName', '');
            setValue('contactName', '');
            setValue('assignedUser', '');
            setValue('assignedUserId', null);
            setValue('leadId', null);
        }
        
        // Handle project selection - auto-populate contact and assigned user
        if (name === 'projectName' && value) {
            const selectedLead = allLeads.find(lead => lead.id.toString() === value);
            if (selectedLead) {
                const matchingContact = contactOptions.find(
                    contact => contact.value === selectedLead.contactId?.toString()
                );
                
                const matchingUser = userOptions.find(
                    user => user.value === selectedLead.assignedUserId?.toString()
                );
                
                setValue('projectName', value, { shouldValidate: false });
                setValue('contactName', matchingContact ? matchingContact.value : '', { shouldValidate: false });
                setValue('assignedUser', matchingUser ? matchingUser.value : '', { shouldValidate: false });
                setValue('assignedUserId', selectedLead.assignedUserId || null, { shouldValidate: false });
                setValue('leadId', selectedLead.id, { shouldValidate: false });
            }
        }

        // Handle assigned user selection (when manually changed)
        if (name === 'assignedUser' && value) {
            const selectedUser = userOptions.find(user => user.value === value);
            if (selectedUser) {
                setValue('assignedUser', value, { shouldValidate: false });
                setValue('assignedUserId', parseInt(selectedUser.value), { shouldValidate: false });
            }
        }
    }, [setValue, fetchProjectOptions, allLeads, contactOptions, userOptions]);

    const validateForm = (data) => {
        const errors = {};
        if (!data.clientName) {
            errors.clientName = 'Client Name is required';
        }
        if (!data.taskTitle || !data.taskTitle.trim()) {
            errors.taskTitle = 'Task Title is required';
        }
        if (!data.type) {
            errors.type = 'Type is required';
        }
        if (!data.priority) {
            errors.priority = 'Priority is required';
        }
        if (!data.status) {
            errors.status = 'Status is required';
        }
        return errors;
    };

    // ✅ PERFORMANCE: Handle form submit - prepare data and call parent onSubmit
    const onSubmitForm = useCallback(async (data) => {
        if (isSubmitting) {
            return;
        }

        const validationErrors = validateForm(data);
        
        if (Object.keys(validationErrors).length > 0) {
            // Set errors in form state using react-hook-form's setError
            Object.keys(validationErrors).forEach(key => {
                setError(key, {
                    type: 'manual',
                    message: validationErrors[key]
                });
            });
            // Scroll to first error
            const firstErrorField = Object.keys(validationErrors)[0];
            const errorElement = document.querySelector(`[name="${firstErrorField}"]`) || 
                              (firstErrorField === 'documents' ? document.querySelector('.dropzone') : null);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        try {
            setIsSubmitting(true);

            // Filter out existing files (they're already on the server)
            const newFiles = documents.filter(file => !file.isExisting);

            // ✅ Get display names for email notifications
            const selectedClient = clientOptions.find(c => c.value === data.clientName);
            const selectedContact = contactOptions.find(c => c.value === data.contactName);
            const selectedProject = projectOptions.find(p => p.value === data.projectName);
            const selectedUser = userOptions.find(u => u.value === data.assignedUser);

            const formDataWithFiles = {
                ...data,
                documents: newFiles,
                // Add display names for email templates
                clientNameDisplay: selectedClient?.label || data.clientName,
                contactNameDisplay: selectedContact?.label || data.contactName,
                projectNameDisplay: selectedProject?.label || data.projectName,
                assignedUserDisplay: selectedUser?.label || data.assignedUser
            };

            // Handle both promise and non-promise returns.
            // Parent may return false (caught internally) to signal failure without throwing.
            const result = onSubmit(formDataWithFiles);

            if (result && typeof result.then === 'function') {
                const success = await result;
                if (success === false) return; // parent signalled failure — keep form data
            }

            // Reset form and close on successful submission
            reset({
                clientName: '',
                contactName: '',
                projectName: '',
                assignedUser: '',
                assignedUserId: null,
                taskTitle: '',
                type: '',
                dueDate: '',
                priority: '',
                status: '',
                description: '',
                leadId: null,
                documents: []
            });
            setDocuments([]);
            if (onClose) onClose();

        } catch (error) {
            const errorMessage = error.response?.data?.error || 
                                error.response?.data?.message || 
                                error.message || 
                                'Failed to save task';
            showAlert('danger', errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, documents, onSubmit, onClose, reset, setError, clientOptions, contactOptions, projectOptions, userOptions]);

    const handleClose = () => {
        
        
        // Reset form when closing
        reset({
            clientName: '',
            contactName: '',
            projectName: '',
            assignedUser: '',
            assignedUserId: null,
            taskTitle: '',
            type: '',
            dueDate: '',
            priority: '',
            status: '',
            description: '',
            leadId: null,
            documents: []
        });
        setDocuments([]);
        setProjectOptions([]);
        isInitializingRef.current = false;
        if (onClose) onClose();
    };


    // Cleanup effect: reset modal body state when component unmounts.
    useEffect(() => {
        return () => {
            // React owns the backdrop DOM (createPortal); only reset body state here.
            // Remove modal-open class from body
        };
    }, []);

    const onDrop = useCallback((acceptedFiles) => {
        // TaskForm supports single file upload
        const file = acceptedFiles[0];
        if (file) {
            const fileData = {
                name: file.name,
                size: `${(file.size / 1024).toFixed(1)} KB`,
                url: URL.createObjectURL(file),
                file
            };
            setDocuments([fileData]); // Only keep 1 file
        }
        // Clear document error when file is uploaded
        setError('documents', { type: 'manual', message: '' }, { shouldFocus: false });
    }, [setError]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        multiple: false,
        accept: {
            'application/pdf': ['.pdf']
        }
    });


    return (
        <>
            <style>{`
                #modal-task .inv-section-label {
                  font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
                  text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
                  display: flex; align-items: center; gap: 0.4rem;
                }
                #modal-task .inv-section-label::after {
                  content: ''; flex: 1; height: 1px; background: #e9ecef;
                }
                #modal-task .inv-footer {
                  border-top: 1px solid #e9ecef !important;
                  background: #fff;
                  padding: 1rem 1.5rem;
                  display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
                  flex-shrink: 0;
                }
                #modal-task .btn-inv-save {
                  display: inline-flex; align-items: center; gap: 0.4rem;
                }
                #modal-task .btn-inv-save:disabled {
                  opacity: 0.6; cursor: not-allowed;
                }
                #modal-task .inv-required-star { color: #e53935; margin-left: 2px; }
            `}</style>
            {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
            <div
                className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
                id="modal-task"
                tabIndex="-1"
                role="dialog"
            >
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="taskModalLabel">
                                {initialData ? 'Edit Task' : 'Create Task'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                                disabled={isSubmitting}
                            ></button>
                        </div>

                        {/* Alert Component */}
                        {alert && (
                            <div className="modal-body pb-0">
                                <Alert
                                    type={alert.type}
                                    message={alert.message}
                                    onClose={() => setAlert(null)}
                                />
                            </div>
                        )}

                        <form onSubmit={async (e) => {
                            e.preventDefault();
                            
                            // Try react-hook-form's handleSubmit first
                            const result = handleSubmit(
                                onSubmitForm,
                                (errors) => {
                                    // If handleSubmit fails, try calling onSubmitForm directly
                                    const formValues = getValues();
                                    onSubmitForm(formValues);
                                }
                            )(e);
                            
                            // If handleSubmit returns undefined (validation failed), 
                            // the error handler above will handle it
                            if (result === undefined) {
                                // Error handler should have been called
                            }
                        }}>
                            <div className="modal-body">
                                <div className="inv-section-label">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                                    Client &amp; Project
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="clientName"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="clientName"
                                                    label="Client Name"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={clientOptions}
                                                    placeholder={isLoadingData ? "Loading clients..." : "Select client"}
                                                    required={true}
                                                    error={errors.clientName?.message}
                                                    disabled={disableCompanyField || !clientOptions.length || isLoadingData || isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label,name"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="projectName"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="projectName"
                                                    label="Project Name"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={projectOptions}
                                                    placeholder={!watchedClient ? "Please select a client first" : projectOptions.length === 0 ? "No projects available" : "Select project (optional)"}
                                                    required={false}
                                                    error={errors.projectName?.message}
                                                    disabled={!watchedClient || isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="contactName"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="contactName"
                                                    label="Contact Name"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={contactOptions}
                                                    placeholder={!watchedClient ? "Please select a client first" : "Select contact"}
                                                    required={false}
                                                    error={errors.contactName?.message}
                                                    disabled={!watchedClient || isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="assignedUser"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="assignedUser"
                                                    label="Assigned To"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={userOptions}
                                                    placeholder={isLoadingData ? "Loading users..." : "Select assigned user"}
                                                    required={false}
                                                    error={errors.assignedUser?.message}
                                                    disabled={(!hasViewGlobalPermission && hasViewOwnPermission) || isLoadingData || isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label"
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-body">
                                <div className="inv-section-label">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                    Task Details
                                </div>
                                <div className="row">
                                    <div className="col-md-12 mb-3">
                                        <Controller
                                            name="taskTitle"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <label className="form-label required">Task Title</label>
                                                    <input
                                                        type="text"
                                                        {...field}
                                                        className={`form-control ${errors.taskTitle ? 'is-invalid' : ''}`}
                                                        placeholder="Enter task title"
                                                        disabled={isSubmitting}
                                                    />
                                                    {errors.taskTitle && (
                                                        <div className="invalid-feedback">{errors.taskTitle.message}</div>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="type"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="type"
                                                    label="Type"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={typeOptions}
                                                    placeholder={isLoadingData ? "Loading types..." : "Select type"}
                                                    required={true}
                                                    error={errors.type?.message}
                                                    disabled={isLoadingData || isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="dueDate"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <label className="form-label">Due Date</label>
                                                    <input
                                                        type="date"
                                                        {...field}
                                                        className="form-control"
                                                        disabled={isSubmitting}
                                                    />
                                                </>
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="priority"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="priority"
                                                    label="Priority"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={priorityOptions}
                                                    placeholder="Select priority"
                                                    required={true}
                                                    error={errors.priority?.message}
                                                    disabled={isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <Controller
                                            name="status"
                                            control={control}
                                            render={({ field }) => (
                                                <AppSelect
                                                    name="status"
                                                    label="Status"
                                                    value={field.value}
                                                    onChange={handleSelectChange}
                                                    options={statusOptions}
                                                    placeholder="Select status"
                                                    required={true}
                                                    error={errors.status?.message}
                                                    disabled={isSubmitting}
                                                    valueField="value"
                                                    labelField="label"
                                                    searchField="label"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <Controller
                                            name="description"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <label className="form-label">Description</label>
                                                    <textarea
                                                        {...field}
                                                        className="form-control"
                                                        placeholder="Enter task description"
                                                        rows={3}
                                                        disabled={isSubmitting}
                                                    />
                                                </>
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-body">
                                <div className="inv-section-label">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    Document
                                </div>
                                <div className="mb-3 mt-1">
                                    <div className="text-muted small">Upload PDF file (optional)</div>
                                </div>
                                <div
                                    {...getRootProps()}
                                    className={`dropzone ${isDragActive ? 'active' : ''} ${errors.documents ? 'border-danger' : ''}`}
                                    style={{
                                        border: `2px dashed ${errors.documents ? '#dc3545' : '#ccc'}`,
                                        borderRadius: '4px',
                                        padding: '20px',
                                        textAlign: 'center',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        backgroundColor: isDragActive ? '#f8f9fa' : 'white',
                                        opacity: isSubmitting ? 0.6 : 1
                                    }}
                                >
                                    <input {...getInputProps()} disabled={isSubmitting} />
                                    {documents.length > 0 ? (
                                        <div className="uploaded-files">
                                            {documents.map((file, index) => (
                                                <div key={`${file.name}-${index}`} className="card mb-2">
                                                    <div className="card-body p-2">
                                                        <div className="d-flex align-items-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-type-pdf text-danger me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                                                                <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
                                                                <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
                                                                <path d="M17 18h2" />
                                                                <path d="M20 15h-3v6" />
                                                                <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
                                                            </svg>
                                                            <div className="file-info flex-grow-1">
                                                                <div className="text-truncate" style={{ fontSize: '14px', maxWidth: "300px" }}>
                                                                    {file.name}
                                                                    {file.isExisting && <span className="badge bg-blue-lt ms-2">Existing</span>}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {file.isExisting ? 'Already uploaded' : file.size}
                                                                </small>
                                                            </div>
                                                            {file.isExisting && file.url && (
                                                                <a
                                                                    href={file.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-icon btn-ghost-primary btn-sm me-1"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                                                                        <path d="M7 11l5 5l5 -5" />
                                                                        <path d="M12 4l0 12" />
                                                                    </svg>
                                                                </a>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-icon btn-ghost-danger btn-sm"
                                                                disabled={isSubmitting}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDocuments([]);
                                                                    // Set error when document is removed (since it might be required)
                                                                    setError('documents', {
                                                                        type: 'manual',
                                                                        message: ''
                                                                    }, { shouldFocus: false });
                                                                }}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                    <path d="M18 6l-12 12" />
                                                                    <path d="M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="text-center mt-2">
                                                <small className="text-muted">Drop file or click to select (PDF only)</small>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-cloud-upload mb-2" width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                                                <path d="M9 15l3 -3l3 3" />
                                                <path d="M12 12l0 9" />
                                            </svg>
                                            <small className="text-center text-muted">
                                                {isDragActive ? "Drop PDF file here..." : "Drop PDF file here or click to select"}
                                            </small>
                                            <small className="text-muted mt-1">PDF file only</small>
                                        </div>
                                    )}
                                </div>
                                {errors.documents && (
                                    <div className="invalid-feedback d-block mt-2">{errors.documents.message}</div>
                                )}
                            </div>
                            <div className="inv-footer">
                                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                                    {isSubmitting ? 'Saving task…' : (
                                        <span style={{ color: '#9aa5b1' }}>
                                            <span className="inv-required-star">*</span> Required fields
                                        </span>
                                    )}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-inv-save"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
});

TaskForm.displayName = 'TaskForm';

TaskForm.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    disableCompanyField: PropTypes.bool,
    defaultClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hasViewOwnPermission: PropTypes.bool,
    hasViewGlobalPermission: PropTypes.bool
};

export default TaskForm;

